"use client";

import { IconMenu2, IconX } from "@tabler/icons-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { NAV_LINKS } from "@/lib/constants";

export function SiteHeader() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Sur l'accueil, le hero est plein cadre jusqu'en haut de l'ecran : la navbar
  // flotte par-dessus l'image (transparente) tant qu'on est en haut de page, puis
  // se "givre" en fond creme une fois scrollee (lisibilite sur le contenu clair).
  // Seuil > 48px (et non 8) pour un declenchement franc, jamais a la moindre
  // micro-impulsion de trackpad. Hysteresis pour eviter le clignotement au seuil.
  useEffect(() => {
    const onScroll = () =>
      setScrolled((prev) => (prev ? window.scrollY > 24 : window.scrollY > 48));
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (pathname === "/login" || pathname === "/construction" || pathname.startsWith("/dashboard"))
    return null;

  const closeMenu = () => setOpen(false);

  const isHome = pathname === "/";
  // Overlay transparent : uniquement sur l'accueil, en haut de page, menu ferme.
  const overlay = isHome && !scrolled && !open;

  const isActive = (href: string) =>
    href === "/" ? pathname === "/" : pathname === href || pathname.startsWith(`${href}/`);

  return (
    <header
      className={`${isHome ? "fixed" : "sticky"} top-0 inset-x-0 z-40 transition-[background-color,box-shadow,backdrop-filter] duration-500 ease-out ${
        overlay
          ? "on-dark-image bg-transparent shadow-none"
          : "bg-(--background)/90 backdrop-blur-md shadow-[0_1px_0_0_#e5dfcf8c,0_12px_30px_-20px_#1f29204d]"
      }`}
    >
      <div className="px-6 md:px-10 lg:px-16">
        {/* Hauteur de navbar constante : aucun mouvement de layout pendant le
            scroll, seul le fond/texte fait un crossfade -> transition propre. */}
        <div className="mx-auto max-w-7xl flex items-center justify-between gap-4 py-4 md:py-5">
          {/* En haut de l'accueil, le logo vit centre dans le hero : celui de la
              navbar s'efface tant que la barre est transparente et reapparait en
              fondu une fois givree (scroll ou menu ouvert). Padding constant :
              la hauteur de la navbar ne bouge jamais. */}
          <Link
            href="/"
            aria-label="Symbiosis Paysage Services, accueil"
            aria-hidden={overlay}
            tabIndex={overlay ? -1 : 0}
            className={`inline-flex items-center -ml-3 rounded-full px-3 py-1.5 transition-opacity duration-500 ease-out ${
              overlay ? "opacity-0 pointer-events-none" : "opacity-100"
            }`}
          >
            <Image
              src="/images/logo-symbiosis-full.webp"
              alt="Symbiosis Paysage Services"
              width={1360}
              height={387}
              priority
              className="h-10 w-auto md:h-12"
            />
          </Link>

          <nav
            aria-label="Navigation principale"
            className="hidden md:flex items-center gap-7 text-sm"
          >
            {NAV_LINKS.map((link) => {
              const active = isActive(link.href);
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  aria-current={active ? "page" : undefined}
                  className={`relative py-1 transition-colors duration-300 ${
                    // text-(--text-primary) (variable runtime) et non text-text-primary
                    // (valeur figée au build par @theme inline) : en overlay sur le
                    // hero, .on-dark-image remappe la variable en crème → liens lisibles.
                    active
                      ? "text-(--text-primary)"
                      : `text-(--text-primary)/70 ${
                          overlay ? "hover:text-(--text-primary)" : "hover:text-primary-light"
                        }`
                  }`}
                >
                  {link.label}
                  {active ? (
                    <span
                      aria-hidden
                      className={`absolute -bottom-1 left-0 right-0 h-px ${
                        overlay ? "bg-current" : "bg-primary-light"
                      }`}
                    />
                  ) : null}
                </Link>
              );
            })}
          </nav>

          <button
            type="button"
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={open}
            aria-controls="mobile-nav"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden p-2 -mr-2 text-(--text-primary) transition-colors duration-300"
          >
            {open ? (
              <IconX size={22} stroke={1.75} aria-hidden />
            ) : (
              <IconMenu2 size={22} stroke={1.75} aria-hidden />
            )}
          </button>
        </div>
      </div>

      {open ? (
        <nav
          id="mobile-nav"
          aria-label="Navigation mobile"
          className="md:hidden border-t border-(--border)/40 bg-background"
        >
          <ul className="px-6 py-3 flex flex-col">
            {NAV_LINKS.map((link) => {
              const active = isActive(link.href);
              return (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    aria-current={active ? "page" : undefined}
                    onClick={closeMenu}
                    className={`block py-3 text-base ${
                      active
                        ? "text-text-primary"
                        : "text-(--text-primary)/75 hover:text-text-primary"
                    }`}
                  >
                    {link.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      ) : null}
    </header>
  );
}
